import { randomUUID } from 'node:crypto'
import { ATLAS_WEB_APPLICATION } from '@katoa/schema-provider'
import {
  data,
  href,
  isRouteErrorResponse,
  redirect,
  type ShouldRevalidateFunction,
  useLoaderData,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { extractErrorMessage } from '../api/extract-error-message'
import { providersClient } from '../api/providers-client'
import { resolveZipLocation } from '../api/zip-location.server'
import { BROWSE_BY_CITY } from '../cities'
import { SiteHeader } from '../components/header'
import { LocationPicker } from '../components/location-picker'
import { ProviderCard } from '../components/provider-card'
import { ResultsHeading } from '../components/results-heading'
import { SearchForm } from '../components/search-form'
import { BackHomeLink, StatusBlock, StatusScreen, TryAgainButton } from '../components/status-screen'
import { logger } from '../instrumentation.server'
import { listSentence } from '../list-sentence'
import { fitDescription, fitTitle, pageMeta, TITLE_SUFFIX } from '../seo'
import { SITE_NAME } from '../site'
import {
  formatCityZipSlug,
  formatLocationLabel,
  formatProviderSlug,
  formatResolvedLocationLabel,
  formatZipSlug,
  parseLocation,
  parseLocationSlug,
  slugify,
} from '../slug'
import { DEFAULT_SPECIALTY_SLUG, specialtyForSlug } from '../specialties'
import { breadcrumbList, itemList } from '../structured-data'
import { isUsRegionCode } from '../us-regions'
import type { Route } from './+types/results'

/**
 * The providers a results page can actually render, in grid order.
 *
 * A provider with no name can't be rendered as a card or linked to (the detail
 * slug is built from the name), so it isn't a result. Whitespace-only counts as
 * no name: slugify would strip it to '', leaving a blank card behind a
 * "npi-<npi>-" link.
 *
 * Shared by `meta` and the component rather than filtered in each: the heading
 * count, the grid and the `ItemList` markup all have to agree, and the markup
 * claiming more results than the page shows is exactly the kind of mismatch
 * Google treats as a data-quality problem.
 */
const renderable = <T extends { name?: string | null | undefined }>(providers: T[]): (T & { name: string })[] =>
  providers.filter((provider): provider is T & { name: string } => provider.name != null && provider.name.trim() !== '')

// The description every page fell back to before VIT-18200, and still does wherever
// there is no roster to name: the loader threw, the search matched nobody, or the
// location is outside our coverage so no search ran at all.
const GENERIC_DESCRIPTION =
  'Care providers matched on cost, quality, and the actual health outcomes of other patients like you.'

/**
 * The `<meta name="description">` for a results page (VIT-18200).
 *
 * The old description was the same 90-character tail on every indexed page, with
 * only the city and specialty varying — so Google discarded it and built its own
 * snippet out of the provider roster instead. This names providers from that same
 * roster, under our control and correctly punctuated, which is the reliable half
 * of the fix (the h1 change is best-effort against an extractor we can't observe).
 *
 * Names without post-nominals, deliberately: the joined form is "Patricia Olson,
 * MD", and three of those in a comma-separated list reads as six people. The card
 * and the avatar alt still carry the credentials — see provider-name.ts.
 *
 * Candidates shed one name at a time so `fitDescription` keeps as many as the
 * 160-character budget allows; a long plan name eats the room, and the boilerplate
 * is the floor under all of it.
 */
const resultsDescription = (countedLabel: string | null, accepting: string, rendered: { name: string }[]): string => {
  if (countedLabel == null) return GENERIC_DESCRIPTION

  const boilerplate = `${countedLabel}${accepting}, matched on cost, quality, and the actual health outcomes of other patients like you.`
  const names = rendered.slice(0, 3).map((provider) => provider.name.trim())

  // Three names, then two, then one — descending preference, the contract
  // `fitDescription` takes.
  //
  // A comma, not an em dash. An em dash sets the clause off more cleanly (the
  // location label already ends in a comma), but em dashes in generated copy read
  // as machine-written, which is the opposite of what a SERP snippet needs to do.
  // "Portland, OR, including …" is ordinary English and costs nothing.
  const named = names.map(
    (_, index) =>
      `${countedLabel}${accepting}, including ${listSentence(names.slice(0, names.length - index))}. Compare cost, quality and real patient outcomes.`,
  )

  const [preferred = boilerplate, ...fallbacks] = [...named, boilerplate]
  return fitDescription(preferred, ...fallbacks)
}

export const meta = ({ loaderData, params }: Route.MetaArgs): Route.MetaDescriptors => {
  const specialty = specialtyForSlug(params.specialty ?? '')

  // `loaderData` is undefined when the loader threw (404/500) and the
  // ErrorBoundary renders instead — fall back to the generic wording rather than
  // a broken title and description naming a search that didn't resolve.
  const search = loaderData?.search

  // Names the applied filter so a shared filtered link is self-describing.
  // Absent whenever `?insurance=` didn't resolve — the page is unfiltered then,
  // and the title must not claim otherwise.
  const accepting = loaderData?.insurance != null ? ` accepting ${loaderData.insurance.displayName}` : ''

  // "Cardiologists in Portland, OR" — the page named without its result count or
  // its insurance filter, for the breadcrumb's last rung and the ItemList. The
  // filter belongs to the query string, which the canonical drops. The count is
  // out for a different reason now that the title carries one (VIT-18152): a rung
  // name that moved with the roster would keep re-describing the same page, and
  // the ItemList already states the total as `numberOfItems`.
  const searchLabel = search == null ? null : `${search.specialtyPluralLabel} in ${search.locationLabel}`

  // "74 Dermatologists in New York, NY" — the same sentence the h1 renders, for
  // the title and the description (VIT-18152). A visitor scanning a SERP or a
  // shared card learns the page has a roster worth opening before they open it.
  //
  // Counted off `renderable`, the filter the grid and the ItemList also apply, so
  // the tab, the heading and the markup cannot state different totals.
  //
  // Zero falls back to the count-free label: "0 Dermatologists in New York, NY"
  // advertises an empty page, and the wording is the one thing about that page
  // that can still earn the click. It also covers the outside-US branch, where
  // `providers` is null because no search ran at all.
  const rendered = renderable(loaderData?.providers?.recommendations ?? [])
  const count = rendered.length
  const countedLabel =
    search == null || count === 0
      ? searchLabel
      : `${count} ${count === 1 ? search.specialtyLabel : search.specialtyPluralLabel} in ${search.locationLabel}`

  // The loader's decision rather than the echoed params: a city--region--zip
  // page is self-referencing, and a zip- page points at the pretty
  // city--region--zip form the loader resolved it to (VIT-18219). Also
  // self-referencing except where two picker rows share one NUCC code: those
  // two URLs return the same roster, so the duplicate points at the primary and
  // search engines index one of them (VIT-17746 item 5 / the pre-existing
  // behaviour). Carries no query string, so the arbitrary `?insurance=<uuid>`
  // values this route accepts stop competing as distinct indexable URLs.
  const canonicalPath =
    loaderData?.canonicalLocation == null
      ? undefined
      : href('/us/:location/:specialty', {
          location: loaderData.canonicalLocation,
          specialty: specialty?.canonicalSlug ?? params.specialty ?? '',
        })

  return [
    ...pageMeta({
      // The count spends about three of the 70 characters VIT-18071 budgets a
      // title, which no unfiltered page can feel — the longest specialty and a
      // long city still land inside it ("22 Primary Care Providers in Colorado
      // Springs, CO | Vital Care Finder" is 68). A filtered one can: the plan name
      // is arbitrary upstream text. So the brand suffix is what gives way first,
      // as on provider.tsx and for the same reason — `og:site_name` still carries
      // it, and the count and the plan are what make the title this page's.
      title:
        countedLabel == null
          ? `Your matched doctors${TITLE_SUFFIX}`
          : fitTitle(`${countedLabel}${accepting}${TITLE_SUFFIX}`, `${countedLabel}${accepting}`),
      description: resultsDescription(countedLabel, accepting, rendered),
      canonicalPath,
    }),

    // Only on a page that resolved. Two rungs, not three: the grammar has a
    // `/us/{location}` level (docs/url-structure.md) but no route serves it, and
    // Google requires `item` on every rung except the last — so a "Portland, OR"
    // middle rung would link a 404.
    ...(searchLabel == null
      ? []
      : [
          { 'script:ld+json': breadcrumbList([{ name: SITE_NAME, path: href('/') }, { name: searchLabel }]) },
          // Marks the city × specialty matrix as curated, ordered result sets
          // rather than thin near-duplicate pages. Same path the card links to.
          {
            'script:ld+json': itemList(
              searchLabel,
              renderable(loaderData?.providers?.recommendations ?? []).map((provider) => ({
                name: provider.name,
                path: href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) }),
              })),
            ),
          },
        ]),
  ]
}

// Forward any loader-set headers onto the outgoing response. Cache-Control is
// set only by the loader's success return, so a thrown 404/500 never picks it
// up and stays uncached at the edge — this route exports its own ErrorBoundary,
// and React Router runs the boundary route's `headers` fn, so a flat headers
// export would stamp s-maxage onto error responses. Mirrors provider.tsx.
export const headers: Route.HeadersFunction = ({ loaderHeaders }) => loaderHeaders

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentParams, nextParams, nextUrl, currentUrl }) => {
  // The search lives in the :location/:specialty path segments, so a new search
  // must re-run the loader; opening/closing a provider detail keeps the same
  // params and skips the reload. The query-string check covers anything else
  // (e.g. mock params) that should refresh results.
  return (
    currentParams['location'] !== nextParams['location'] ||
    currentParams['specialty'] !== nextParams['specialty'] ||
    nextUrl.search !== currentUrl.search
  )
}

// Shared-cache the results page (and its `.data` sibling) at the vit.al edge.
// The honor-origin cache policy in sst.config.ts has defaultTtl 0, so without
// this header every request reaches the SSR Lambda. max-age lets the browser
// reuse the page for an hour without a revalidation round-trip; s-maxage lets
// CloudFront serve it. Unlike home.tsx / support.tsx / terms.tsx / privacy.tsx,
// which keep max-age=0, results are safe to hold in the browser cache.
//
// The invariant this depends on: everything the loader returns is derived only
// from the URL — the path params AND the query string (`?insurance=`) — never
// from request headers or cookies. `reactHonorOriginCachePolicy` keys the query
// string except for the tracking params in app/cache-key.ts (VIT-17932), which no
// loader reads. Headers are forwarded by `reactGeoOriginRequestPolicy` and keyed
// by neither policy — so a header-derived value would serve one viewer's data to
// everyone. Two consequences:
//
//  * The outside-US branch below tests the :location region code, not the
//    viewer's geo.
//  * The typeahead's `state` relevance hint comes from client-side geo through
//    the `no-store` /resource/insurance-search route, never from this loader.
//
// Set on the loader's non-throwing returns only, so a thrown 404/500 never picks
// it up and stays uncached at the edge (see the `headers` export above).
const CACHE_CONTROL = 'public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400'

/** The resolved `?insurance=` plan — the filter applied, and the label shown. */
type ResolvedInsurance = { id: string; displayName: string; carrierName: string; state: string | null }

// A plan id is a Ribbon uuid. Cheap shape guard so a junk or hostile query
// param costs no upstream round trip.
const PLAN_ID_RE = /^[A-Za-z0-9-]{1,64}$/

/**
 * Resolve `?insurance=<uuid>` to the catalog row it names. Mirrors
 * find-doctor.service.ts's `resolvePlanInsurance`.
 *
 * Every miss degrades identically and non-fatally: no filter applied, no filter
 * named, param ignored. A bad query param must never error a page whose path is
 * valid — and never redirect to strip it either, which would break the back
 * button and add a cache-key hop.
 *
 * A row with no carrier is treated as a miss rather than filtering on the plan
 * alone: `buildInsuranceLadder` gives a plan-only context a single rung with no
 * unfiltered fallback (a cliff), and provider-api only synthesises
 * `insuranceMatches` when a carrier name is set.
 */
const resolveInsurance = async (raw: string | null, nuccCode: string): Promise<ResolvedInsurance | null> => {
  const id = raw?.trim() ?? ''
  if (id === '' || !PLAN_ID_RE.test(id)) return null

  try {
    const response = await providersClient.getRibbonInsurance({ params: { id } })
    if (response.status !== 200) {
      // 404 is the common case — a stale shared link, a hand-typed id, or an id
      // from the live Ribbon passthrough (whose ids this endpoint doesn't know).
      logger.warn({ responseStatus: response.status, nuccCode }, 'results.insurance_plan_unresolved')
      return null
    }

    const carrierName = response.body.carrierName?.trim()
    if (carrierName == null || carrierName === '') {
      logger.info({ nuccCode }, 'results.insurance_plan_has_no_carrier')
      return null
    }

    // `displayName` is non-nullable in the contract but not non-empty, and it is
    // the only string four consumers render. A blank one would apply a filter
    // nothing on the page could name — "accepting " in the title, a heading
    // trailing off — so treat it as unresolvable, exactly like a missing carrier.
    const displayName = response.body.displayName.trim()
    if (displayName === '') {
      logger.warn({ nuccCode, carrierName }, 'results.insurance_plan_has_no_display_name')
      return null
    }

    logger.info({ nuccCode, carrierName }, 'results.insurance_plan_resolved')
    return {
      id: response.body.id,
      displayName,
      carrierName,
      state: response.body.state,
    }
  } catch (error) {
    logger.warn({ error, nuccCode }, 'results.insurance_plan_lookup_failed')
    return null
  }
}

export const loader = async ({ params, request }: Route.LoaderArgs) => {
  const { location, specialty: specialtySlug } = params

  const specialty = specialtyForSlug(specialtySlug)
  const resolvedLocation = parseLocation(location)

  // Pre-VIT-18219 city--region URLs for a curated city 301 to that city's
  // primary-ZIP page — every URL the site ever emitted in the old form (the
  // sitemap and browse-by-city) is one of these 21. The query string rides
  // along so a filtered old link keeps its filter. Any other malformed
  // location 404s below, as before.
  if (resolvedLocation == null && specialty != null) {
    const legacy = parseLocationSlug(location)
    const curated =
      legacy == null
        ? undefined
        : BROWSE_BY_CITY.find(
            (row) =>
              slugify(row.city) === slugify(legacy.city) && row.region.toUpperCase() === legacy.region.toUpperCase(),
          )
    if (curated != null) {
      throw redirect(
        `${href('/us/:location/:specialty', {
          location: formatCityZipSlug(curated.city, curated.region, curated.postalCode),
          specialty: specialtySlug,
        })}${new URL(request.url).search}`,
        { status: 301, headers: { 'Cache-Control': CACHE_CONTROL } },
      )
    }
  }

  // A specialty and a well-formed location — either <city>--<region>--<zip> or
  // zip-<5 digits> — are required to search. The route pattern makes both
  // segments mandatory, but either can arrive empty/malformed via a hand-built
  // URL (the form itself always submits both). Surface the error state rather
  // than a blank page.
  if (!specialty || !resolvedLocation) {
    const message = 'Provider search skipped: unresolvable specialty or location path segment'
    // `status` is a Datadog reserved attribute (remapped to log severity), so
    // the HTTP code goes under `responseStatus` to survive ingestion.
    logger.error({ responseStatus: 404 }, message)
    throw data('Not Found', { status: 404 })
  }

  // Resolve the ZIP to its city/state. For a zip- segment this labels the
  // heading and <title> with a place rather than 5 digits (VIT-17833) and
  // supplies the pretty canonical (VIT-18219); for a city--region--zip segment
  // it validates the pairing below. Started here and awaited alongside the
  // provider search, so it costs no extra wall-clock on the zip- path. The page
  // stays shared-cacheable: this is still derived purely from the path.
  const zipLocationPromise = resolvedLocation.kind === 'intl' ? null : resolveZipLocation(resolvedLocation.postalCode)

  // A city--region--zip segment must name a ZIP that actually belongs to that
  // city/state (VIT-18219): several ZIPs share one city name, so the pairing is
  // the page's identity, and a mismatched or unknown ZIP is a 404, not a
  // search. Compared slug-to-slug so hyphen/apostrophe variants (WINSTON-SALEM,
  // COEUR D ALENE) can't false-404.
  if (resolvedLocation.kind === 'city') {
    const resolution = await zipLocationPromise
    // A FAILED lookup is not a mismatch: the sitemap surface depends on this
    // endpoint now, and a ZIP outage must not 404 every curated page. Report it
    // like any other upstream failure (500 + retry state).
    if (resolution?.status === 'error') {
      const message = 'ZIP location lookup failed while validating the city-state-zip path segment'
      logger.error({ postalCode: resolvedLocation.postalCode }, message)
      throw new Error(message)
    }
    const matches =
      resolution?.status === 'resolved' &&
      slugify(resolution.city) === slugify(resolvedLocation.city) &&
      resolution.state.toUpperCase() === resolvedLocation.region.toUpperCase()
    if (!matches) {
      logger.error(
        { responseStatus: 404, postalCode: resolvedLocation.postalCode },
        'Provider search skipped: ZIP does not match the city-state path segment',
      )
      throw data('Not Found', { status: 404 })
    }
  }

  // Display strings for the heading and <title>. Resolved here rather than
  // re-derived in the component so the path params are parsed exactly once, and
  // before the outside-US branch below so both returns carry them (`meta` reads
  // `loaderData.search` unconditionally).
  const search = {
    specialtyLabel: specialty.label,
    specialtyPluralLabel: specialty.pluralLabel,
    locationLabel: formatResolvedLocationLabel(resolvedLocation),
  }

  // Atlas only has US provider data, so a location we can't cover returns nothing
  // — skip the API call and let the page render the "outside our coverage area"
  // empty state instead of surfacing a geolocation error (VIT-17624). Derived from
  // the path, never from the viewer's geo — see CACHE_CONTROL.
  //
  // Two path forms land here, and neither is a `zip-` segment: those are always in
  // coverage, because the picker only resolves US ZIPs.
  //
  //  * `intl-` — what SearchForm emits for a viewer outside the US. The country is
  //    explicit here, so it can never be confused with a US state (see
  //    INTL_SLUG_RE in slug.ts).
  //  * a `city--region--zip` slug whose region isn't a US state — the picker's
  //    GU/VI behavior noted below, whose pairing has already been validated above.
  //
  // Testing the discriminant directly (rather than via a precomputed boolean) is
  // what lets the `address` build below narrow `intl` away — every one of them has
  // returned by then.
  if (
    resolvedLocation.kind === 'intl' ||
    (resolvedLocation.kind === 'city' && !isUsRegionCode(resolvedLocation.region))
  ) {
    // `info`, not `error`: a non-US viewer reaching a non-US URL is expected, not
    // a fault — keeping it out of `logger.error` keeps the Datadog error signal
    // clean. Each form logs the fields it actually carries.
    logger.info(
      resolvedLocation.kind === 'intl'
        ? { city: resolvedLocation.city, country: resolvedLocation.country }
        : { city: resolvedLocation.city, region: resolvedLocation.region },
      'Provider search skipped: location outside supported US regions',
    )

    // `providers: null` rather than an empty roster: it pairs with the
    // `outsideUs` discriminant so `useLoaderData` narrows the two returns, and
    // avoids having to name the recommendations element type here.
    //
    // `insurance: null` without resolving: this branch's invariant is that it
    // makes no upstream call at all (VIT-17624). The cost is that the field
    // won't rehydrate on an outside-US URL carrying `?insurance=` — a page that
    // is a dead end anyway.
    return data(
      {
        outsideUs: true as const,
        providers: null,
        search,
        insurance: null,
        // No US state to scope the insurance catalog to — the field stays
        // unfiltered rather than borrowing the viewer's own state (VIT-18001).
        searchState: null,
        canonicalLocation: location,
      },
      { headers: { 'Cache-Control': CACHE_CONTROL } },
    )
  }

  // Resolved after the outside-US short-circuit above, and sequentially rather
  // than alongside the search — the search body depends on the result, which is
  // the critical-data exemption in docs/front-end/react-router.md. Costs one
  // extra serial hop on filtered pages only, reported for free as
  // `up.providers.getRibbonInsurance` in Server-Timing by the timedClient proxy.
  const insurance = await resolveInsurance(new URL(request.url).searchParams.get('insurance'), specialty.nuccCode)

  // searchProviders accepts a postal code or a city/state pair, never both and
  // never coordinates. A ZIP is the more precise centre of the two (the HUD
  // residential ZIP centroid vs a Census place centroid), which is why the
  // picker persists one — and every US URL form now carries one (VIT-18219).
  const address = { postalCode: resolvedLocation.postalCode }

  const [response, zipResolution] = await Promise.all([
    providersClient.searchProviders({
      body: {
        nuccCode: specialty.nuccCode,
        address,
        // Declare the web surface so the search is traced as atlas-web rather than
        // defaulting to the MCP application.
        application: ATLAS_WEB_APPLICATION,
        // atlas-web has no user identity yet; a per-request UUID satisfies the
        // API's required stable-subject field.
        userId: randomUUID(),
        // BOTH, deliberately: buildInsuranceLadder turns plan + carrier into a
        // two-rung ladder that widens plan → carrier when nobody accepts the
        // exact plan. Plan alone is a cliff — the unfiltered rung is appended
        // only when neither is present. Mirrors find-doctor.service.ts.
        // Conditional spread because both fields are `.optional()` and NOT
        // nullable, so an explicit null 400s (exactOptionalPropertyTypes is on).
        ...(insurance != null ? { insurancePlanId: insurance.id, carrierName: insurance.carrierName } : {}),
      },
    }),
    zipLocationPromise,
  ])

  // The zip- page's label/canonical fallbacks treat an unresolved ZIP and a
  // failed lookup alike: bare-ZIP heading, self-canonical — a degraded page,
  // not an error.
  const zip = zipResolution != null && zipResolution.status === 'resolved' ? zipResolution : null

  if (response.status !== 200) {
    // 400/500/503 are the contract's declared error statuses; anything else is
    // outside the contract entirely — same enrichment either way, distinct
    // messages so the two cases stay separable in Datadog.
    const message =
      response.status === 400 || response.status === 500 || response.status === 503
        ? 'Handled error response while running provider search'
        : 'Handled unexpected response status while running provider search'
    // `status` and `message` are Datadog reserved attributes (remapped to log
    // severity / the displayed message), so the HTTP code and the upstream API
    // error go under `responseStatus` / `providerMessage` to survive ingestion.
    // The search params make the failing request reproducible from the log.
    logger.error(
      {
        responseStatus: response.status,
        providerMessage: extractErrorMessage(response.status, response.body),
        nuccCode: specialty.nuccCode,
        // Both US URL forms search by postal code now (VIT-18219), so that is
        // the location the failing request is reproducible from.
        postalCode: resolvedLocation.postalCode,
      },
      message,
    )
    // `handleError` (entry.server.tsx) serializes `cause` via pino's err
    // serializer, so the upstream detail also reaches Datadog on the
    // unhandled-error log entry this throw produces.
    throw new Error(message, {
      cause: { responseStatus: response.status, body: response.body },
    })
  }

  return data(
    {
      outsideUs: false as const,
      providers: response.body,
      insurance,
      // formatLocationLabel, not the raw city: the ZIP dataset mirrors HUD, whose
      // PREF_CITY is ALL-CAPS, so "PORTLAND" has to be title-cased (VIT-17805).
      // Only a zip- page takes its label from the lookup — a city--region--zip
      // page's label already names the ZIP it searched — and the ZIP stays on
      // it, so the heading matches the canonical page it points at (VIT-18219).
      search: {
        ...search,
        ...(resolvedLocation.kind === 'zip' && zip != null
          ? {
              locationLabel: `${formatLocationLabel({ city: zip.city, region: zip.state })} ${resolvedLocation.postalCode}`,
            }
          : {}),
      },
      // The URL meta's <link rel=canonical> points at (VIT-18219). A
      // city--region--zip page is its own canonical — the segment was validated
      // above. A zip- page canonicalizes to the pretty form when the lookup
      // resolved, and to itself when it didn't (PO-box-only HUD ZIPs).
      canonicalLocation:
        resolvedLocation.kind === 'city'
          ? location
          : zip != null
            ? formatCityZipSlug(zip.city, zip.state, resolvedLocation.postalCode)
            : location,
      // The state the insurance typeahead scopes its catalog to (VIT-18001):
      // the place this page searches, which overrides any location picked
      // earlier on the home page. A city--region--zip segment carries it (and
      // the non-US branch above has already returned, so it is a USPS code); a
      // zip- segment takes it from the ZIP lookup, and null when that didn't
      // resolve. Path-derived only, so the page stays shared-cacheable.
      searchState:
        resolvedLocation.kind === 'city' ? resolvedLocation.region.toUpperCase() : (zip?.state.toUpperCase() ?? null),
    },
    { headers: { 'Cache-Control': CACHE_CONTROL } },
  )
}

const Results = () => {
  const loaderData = useLoaderData<typeof loader>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const params = useParams()

  // `providers` is null on the outside-US branch (no API call was made), which
  // flattens to the same empty list as an in-coverage zero-result search — the
  // variant below is what distinguishes the two for the viewer. `renderable` is
  // the same filter `meta` applies, so the heading count, the grid and the
  // ItemList markup can't disagree.
  const providers = renderable(loaderData.providers?.recommendations ?? [])

  const outsideUs = loaderData.outsideUs
  const variant = outsideUs ? 'outside-us' : 'no-providers'
  const insuranceLabel = loaderData.insurance?.displayName ?? null

  return (
    <>
      <SiteHeader />
      <main data-testid="results-page" className="grow-1 px-3 sm:px-4">
        {/* `searchLocation`: this page searches the location in its own URL, so
            the submit goes back there rather than to wherever the viewer is —
            the heading reads off the path, and the two must not disagree
            (VIT-18001). The canonical form, so a `zip-` page submits to the
            city--region--zip page it points at (VIT-18219). */}
        <SearchForm
          simple={true}
          insurance={loaderData.insurance}
          searchState={loaderData.searchState}
          searchLocation={loaderData.canonicalLocation}
        />

        <div className="max-w-7xl mx-auto space-y-4 py-8">
          <ResultsHeading
            count={providers.length}
            specialtyLabel={loaderData.search.specialtyLabel}
            specialtyPluralLabel={loaderData.search.specialtyPluralLabel}
            insuranceLabel={loaderData.insurance?.displayName ?? null}
            location={
              <LocationPicker
                className="text-primary-400 underline underline-offset-4"
                data-testid="results-location-picker"
                // The search lives in the path, so a new location is a navigation.
                // A picked ZIP's city/state/ZIP together build the
                // city--region--zip form (VIT-18219); a ZIP with no city/state
                // in the dataset keeps the zip- form; a GPS-only pick has no
                // searchable location yet (see location.ts), so it persists and
                // leaves the page where it is.
                //
                // No isUsRegionCode guard on the pick: for a US territory outside
                // US_REGION_CODES (GU, VI) the city--region--zip URL renders the
                // accurate "outside our coverage area" state, which beats a
                // zero-result ZIP page.
                onConfirm={(picked) => {
                  const location =
                    picked.city != null && picked.region != null && picked.postalCode != null
                      ? formatCityZipSlug(picked.city, picked.region, picked.postalCode)
                      : picked.postalCode != null
                        ? formatZipSlug(picked.postalCode)
                        : null
                  if (location === null) return
                  navigate(
                    {
                      pathname: href('/us/:location/:specialty', {
                        location,
                        specialty: params['specialty'] ?? DEFAULT_SPECIALTY_SLUG,
                      }),
                      // Carry the query string (e.g. `?insurance=`) so changing
                      // location doesn't silently reset the rest of the search.
                      search: searchParams.toString(),
                    },
                    { viewTransition: true },
                  )
                }}
              >
                {loaderData.search.locationLabel}
              </LocationPicker>
            }
          />

          {providers.length === 0 && (
            // A successful search that matched no one — not an error. The heading
            // above already names the (zero) count, specialty and location, so
            // this block adds the illustration and, crucially, what to do next.
            // The SearchForm at the top of the page is the actual way to adjust
            // the search, so this offers guidance rather than a duplicate control.
            //
            // Two reasons to land here, and they need different advice (VIT-17624):
            // a location outside our coverage has nothing to find whatever you
            // change, so pointing at a nearby location or another specialty would
            // be wrong — say coverage is US-only instead. `outsideUs` is on the
            // wrapper as `data-variant` so one selector covers both.
            <div className="py-8 sm:py-12" data-testid="results-empty" data-variant={variant}>
              <StatusBlock
                variant="empty"
                titleAs="h2"
                title="No matches for this search"
                description={
                  outsideUs
                    ? 'Sorry, Vital Care Finder is currently only supported within the United States.'
                    : // Only offer to drop the insurance filter when there is one —
                      // otherwise the advice names a control the visitor never used.
                      // Clearing it is the combobox's X plus re-submitting, same as
                      // the ZIP field.
                      `We couldn't find any ${loaderData.search.specialtyPluralLabel.toLowerCase()}${
                        insuranceLabel != null ? ` accepting ${insuranceLabel}` : ''
                      } in ${loaderData.search.locationLabel} right now. Try a nearby location, a different specialty${
                        insuranceLabel != null ? ', or removing your insurance filter.' : '.'
                      }`
                }
              />
            </div>
          )}

          {/* A real list, so a screen reader announces "list, N items" before the
            first card instead of reading a bare run of <article>s (VIT-17746).
            `<li className="flex">` plus `w-full` on the card keeps the equal-height
            grid rows the cards had as direct grid children.

            The explicit `role="list"` is NOT redundant here, which is what the two
            waivers below are about: Tailwind's preflight sets `list-style: none` on
            every `ul` (node_modules/tailwindcss/preflight.css), and Safari+VoiceOver
            drop list semantics from an unstyled list — so without the role this
            announces nothing on the one browser/reader pair most likely to be in
            use. Biome's rules read the attribute against the element in isolation
            and can't see the stylesheet.

            Rendered only when it has contents: an empty `<ul role="list">` is still
            a node in the accessibility tree, so a zero-result or outside-US page
            would announce "list, 0 items" straight after the empty-state block said
            the same thing in prose. The `div` this replaced was silent when empty,
            so the announcement is the role's doing. */}
          {providers.length > 0 && (
            // biome-ignore lint/a11y/useSemanticElements: this already IS a <ul> — the role restores the semantics Tailwind's list-style reset costs it in Safari/VoiceOver
            // biome-ignore lint/a11y/noRedundantRoles: not redundant under `list-style: none`, see above
            <ul role="list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {providers.map((provider) => (
                <li key={provider.npi} className="flex">
                  <ProviderCard
                    provider={provider}
                    // Path only, no query string: `/us/*` keys the full query string
                    // at the edge, so forwarding `?insurance=<uuid>` would fragment
                    // the provider page's cache per (provider × plan) — and
                    // `prefetch="viewport"` would fetch each of those `.data`
                    // variants. Nothing is lost: the provider page's back affordance
                    // is `navigate(-1)`, i.e. history, which restores the filtered
                    // results URL intact.
                    detailTo={href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) })}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Two ways to land here (see the loader): a thrown 404 Response for an
  // unresolvable location/specialty path segment, or a thrown Error when the
  // provider-search API failed (4xx/5xx/network). The 404 is a bad URL the
  // viewer can only escape by starting over; anything else is our problem and
  // may clear on a retry. The upstream detail is deliberately not surfaced —
  // it's already logged for Datadog and means nothing to a visitor.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404

  // The route owns the SiteHeader (this boundary replaces the route component,
  // not a layout), so render it here too — without it the error state loses the
  // wordmark and Home navigation.
  return (
    <>
      <SiteHeader />
      {isNotFound ? (
        <StatusScreen
          variant="not-found"
          data-testid="results-error"
          title="That search doesn't look right"
          description="We couldn't make sense of that location or specialty. Start a fresh search to find doctors near you."
        >
          <BackHomeLink />
        </StatusScreen>
      ) : (
        <StatusScreen
          variant="error"
          data-testid="results-error"
          title="Something went wrong"
          description="We couldn't load your results just now — it's on us, not you. Please try again in a moment."
        >
          <TryAgainButton />
          <BackHomeLink />
        </StatusScreen>
      )}
    </>
  )
}

export default Results
